import type { DockviewApi } from 'dockview-react';
import { createContext, useContext } from 'react';

/** Workbench areas the user can show/hide via a toggle control. */
export type ToggleableArea = 'panel' | 'statusBar' | 'auxiliaryBar';

/**
 * The public API surface exposed by `WorkbenchLayout` via `WorkbenchLayoutContext`.
 * Any component inside the workbench tree (header buttons, activityBar actions,
 * command palette, etc.) can call these without going through prop chains
 * (架构方案 §5.7).
 *
 * The `dockview` field gives raw access to the DockviewApi for advanced
 * operations (dynamic tabs, focus, maximize) that are not yet captured as
 * named methods here.
 */
export interface WorkbenchLayoutApi {
  /** Raw DockviewApi — available after onReady fires, null before. */
  readonly dockview: DockviewApi | null;
  /** Current visibility state for the collapsible areas. */
  readonly openAreas: Record<ToggleableArea, boolean>;
  /**
   * Whether each area is enabled by the effective schema (`enabled !== false`),
   * including occupancy overlays. A disabled area is never created and cannot
   * be toggled; consumers should hide its toggle control (架构方案 §5.7).
   */
  readonly enabledAreas: Record<ToggleableArea, boolean>;
  /**
   * Toggle an area's visibility, preserving the user's last size / position.
   * No-op for areas disabled by the schema.
   */
  toggle: (area: ToggleableArea) => void;
  /**
   * Discard the user's customised layout and rebuild the schema default
   * (sizes, positions, visibility), clearing the persisted layout (架构方案
   * §5.7). No-op before the dockview instance is ready.
   */
  reset: () => void;
}

/**
 * No-op value served before the dockview instance is ready. Components that
 * read `openAreas` will render with everything collapsed; `toggle` is a safe
 * no-op so buttons do not throw before the layout mounts.
 */
const NOOP_API: WorkbenchLayoutApi = {
  dockview: null,
  openAreas: { panel: false, statusBar: false, auxiliaryBar: false },
  enabledAreas: { panel: false, statusBar: false, auxiliaryBar: false },
  toggle: () => {},
  reset: () => {},
};

export const WorkbenchLayoutContext =
  createContext<WorkbenchLayoutApi>(NOOP_API);

/**
 * Consume the workbench layout API from anywhere inside the `WorkbenchLayout`
 * component tree (including dockview React portals, which preserve the React
 * Context chain).
 */
export function useWorkbenchLayout(): WorkbenchLayoutApi {
  return useContext(WorkbenchLayoutContext);
}
